import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CallIntelUtils } from '@frontend/api-call-intel';
import { navigateToUrl } from '@frontend/auth';
import { hasACL, CoreACLs, type PortalUser, setLastVisitedPage } from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import { ErrorBoundary, MainBoundary } from '@frontend/error-boundary';
import { INTAKE_FORM_PATH_V2 } from '@frontend/onboarding';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { shell } from '@frontend/shell-utils';
import { WEAVE_COPILOT_PATH } from '@frontend/weave-copilot';
import { Outlet, router, RootRoute } from '@frontend/weave-router';
import { AuthenticatedApp } from '../components/authenticated-app';
import { EnvironmentSwitcher } from '../components/environment-switcher';
import { GlobalSearchView } from '../components/global-search';
import { HistoryControl } from '../components/history-control/history-control';
import { useDocumentTitle } from '../components/use-document-title';
import { VersionInfo } from '../components/version-info';
import { useApp } from '../providers/app.provider';
import { useThirdPartyScripts } from '../third-party-scripts/use-third-party-scripts';

const signInUri = '/sign-in';
export function RootRouteComponent() {
  useRemoveAppLoader();

  const data = RootRoute.useRouteContext();
  const hasShellThemeEnabled = shell.isShell && shell.featureAvailability?.has('shell-theme');
  const showEnvSwitch = hasACL('weave', CoreACLs.DEVTOOLS) || import.meta.env.MODE === 'development';
  const originalURI = window.location.pathname + window.location.search;
  if (originalURI && !originalURI.startsWith(signInUri)) {
    setLastVisitedPage(originalURI);
  }
  useThirdPartyScripts();
  return (
    <>
      <VersionInfo />
      <EnvironmentSwitcher enabled={showEnvSwitch} />
      <ErrorBoundary fallback={MainBoundary}>
        <AppComponent authenticated={data.authenticated} user={data.user}>
          <Outlet />
        </AppComponent>
      </ErrorBoundary>
      {hasShellThemeEnabled && <TitleBarNavigationControls />}
      {hasShellThemeEnabled && <TitleBarGlobalSearch />}
    </>
  );
}

const AppComponent = ({
  authenticated,
  user,
  children,
}: {
  authenticated: boolean;
  user: PortalUser | undefined;
  children: React.ReactNode;
}) => {
  const pathname = router.state.location.pathname;
  const matches = router.state.matches;
  const deepestMatch = matches?.at(-1);
  const isPublicRoute = deepestMatch?.id.startsWith('/_public');
  const isNotFound = router.hasNotFoundMatch();

  useDocumentTitle(pathname);
  const { updateAvailable, currentVersion, shouldPreventUpdate } = useApp();

  const isIntakeForm = pathname === INTAKE_FORM_PATH_V2;
  const isDemoUserFlag = CallIntelUtils.isDemoUser(user?.username);
  const isWeaveCopilotRoute = pathname === WEAVE_COPILOT_PATH;

  useAppUpdater({
    history: router.history,
    updateAvailable,
    enabled: !shouldPreventUpdate && !isDemoUserFlag,
    currentVersion,
  });

  const originalURI = window.location.pathname + window.location.search;
  const shouldRedirectToSignIn =
    !isNotFound && !isPublicRoute && !authenticated && originalURI !== signInUri && originalURI !== '/';
  const shouldWrapInAuthenticatedApp = authenticated && !isPublicRoute && !isIntakeForm && !isWeaveCopilotRoute;

  if (shouldRedirectToSignIn) {
    console.info(`Hit a private route ${originalURI} without being authenticated. Redirecting to ${signInUri}`);
    // use navigateToUrl because we're navigating outside of the router context
    navigateToUrl(signInUri, { reason: 'Hit a private route without being authenticated' });
    return <p>Redirecting</p>;
  }

  if (shouldWrapInAuthenticatedApp) {
    return <AuthenticatedApp>{children}</AuthenticatedApp>;
  }

  return <>{children}</>;
};

const useAppUpdater = ({
  history,
  updateAvailable,
  enabled,
  currentVersion,
}: {
  history: typeof router.history;
  enabled: boolean;
  updateAvailable: boolean;
  currentVersion: string | undefined;
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const unsubscribe = history.subscribe(() => {
      if (enabled) {
        return;
      }
      if (!enabled && updateAvailable) {
        timeoutRef.current = setTimeout(() => {
          console.info('Refreshing app because new version is available (' + currentVersion + ')');
          window.location.reload();
        }, 20);
      }
    });

    return () => {
      unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [history, enabled, updateAvailable, currentVersion]);
};

const TitleBarGlobalSearch = () => {
  const titleBarContentRef = useRef<HTMLElement | null>(null);
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasBubbleNavMode = getFeatureFlagValue('bubbleNavMode');
  const isXsmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const showGlobalSearch = hasBubbleNavMode && isXsmall;

  useEffect(() => {
    if (!titleBarContentRef.current) {
      const element = document.getElementById('weave-shell-title-bar-content');
      if (element) titleBarContentRef.current = element;
    }
  });

  if (!titleBarContentRef.current || showGlobalSearch) return null;
  return createPortal(<GlobalSearchView />, titleBarContentRef.current);
};

const TitleBarNavigationControls = () => {
  const titleBarNavigationRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!titleBarNavigationRef.current) {
      const element = document.getElementById('weave-shell-title-bar-buttons');
      if (element) titleBarNavigationRef.current = element;
    }
  });

  if (!titleBarNavigationRef.current) return null;
  return createPortal(<HistoryControl />, titleBarNavigationRef.current);
};
